import React, { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from 'react';
import { ApiHelper } from './ApiHelper';
import type { Service } from './interfaces/Service';

// Create the context
export interface AppConfig extends Record<string, unknown> {
    services: Service[];
}

type RawConfig = Record<string, unknown> & {
    services?: Record<string, Service> | Service[];
};

const ConfigContext = createContext<AppConfig | null>(null);

function normalizeConfig(rawConfig: RawConfig): AppConfig {
    const rawServices = Array.isArray(rawConfig.services)
        ? rawConfig.services
        : Object.values(rawConfig.services ?? {});

    return {
        ...rawConfig,
        services: rawServices.map(service => {
            const environments = service.environments
                ?? Object.fromEntries((service.Environments ?? []).map(environment => [environment.name, environment]));
            const processes = service.processes
                ?? Object.fromEntries((service.Processes ?? []).map(process => [process.name, process]));

            return {
                ...service,
                environments,
                processes,
                Environments: Object.values(environments),
                Processes: Object.values(processes),
            };
        }),
    };
}

// Create a provider component
export const ConfigProvider = ({ children, config: initialConfig = null }: { children: ReactNode; config?: RawConfig | null }) => {
    const [config, setConfig] = useState<AppConfig | null>(initialConfig ? normalizeConfig(initialConfig) : null);
    const [loading, setLoading] = useState(initialConfig == null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (initialConfig != null) {
            return;
        }

        const fetchConfig = async () => {
            try {
                setLoading(true);
                const response = await ApiHelper<RawConfig>("/api/config");
                setConfig(normalizeConfig(response));
            } catch (err) {
                setError("Failed to load configuration");
            } finally {
                setLoading(false);
            }
        };

        fetchConfig();
    }, [initialConfig]);

    if (loading) {
        return <div>Loading configuration...</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    return (
        <ConfigContext.Provider value={config}>
            {children}
        </ConfigContext.Provider>
    );
};

// Create a custom hook to access the config
export const useConfig = () => {
    const context = useContext(ConfigContext);
    if (!context) {
        throw new Error("useConfig must be used within a ConfigProvider");
    }
    return context;
};
